import React, { useEffect, useState, Fragment, useMemo, useCallback } from 'react';
import TimeRowGroup from '../types/TimePeriodGroupM';
import TimePeriodGroup from './TimePeriodGroup';
import '../Styles/TimePeriod.scss';
import { FontIcon } from '@fluentui/react/lib/Icon';
import { TimeGridMode } from '../types/TimeGridMode';
import {
    SortTimeRows,
    NumberMax2DecimalFormatAsString,
    GetTotalFromTimeRowInDayArray,
    IsDatesNoTimeEqual,
    MathToFixed,
    FormatValue,
    IsWeekend,
    IsHoliday,
    GetHolidayName,
    IsDeadline,
} from './Utils';
import { PeriodDay } from '../types/PeriodDay';
import { ScrollablePane, Stack, Text, TooltipHost, IconButton, DirectionalHint, IButtonStyles } from '@fluentui/react';
import { useId } from '@fluentui/react-hooks';
import { ValidateResponse } from '../Validation/Validator';
import { WorkDayBar } from './WorkDayBar/WorkDayBar';
import { Allocation, TimeEntry, UserRegistrationPeriod, UserReportingPeriodState, WorkDay, WorkType } from '../api/generated/data-contracts';
import { useGridContext } from './Contexts/GridContext/context';
import { useConfigValuesState } from '../hooks/useConfigValue';
import { useTranslation } from 'react-i18next';
import { format } from '../helpers/date';
import { getPeriodDays } from '../helpers/periodDays';
import { useAllocationFormatter } from '../hooks/useAllocationFormatter';

interface TimePeriodProps {
    times: UserRegistrationPeriod[];
    onCellUpdated: (
        time: UserRegistrationPeriod,
        day: PeriodDay,
        te: TimeEntry,
        value: number,
        comment: string,
        flagged: boolean,
        mode: TimeGridMode,
        previousValue: number,
        saveSuccess: Function,
        skipApiCall?: boolean,
    ) => void;
    totalCellUpdated: Function;
    mode: TimeGridMode;
    pinTime: Function;
    deleteTime: Function;
    today: Date;
    submitted: boolean;
    reportPeriodId: string;
    search: number;
    periodIsClosed: boolean;
    startDate: Date;
    endDate: Date;
    deadlineDate: Date;
    timesheetstate: UserReportingPeriodState;
    timesheetmessage: string;
    updateWorkType: (row: UserRegistrationPeriod, workType: WorkType, changeCompleted: Function) => void;
    validationErrors: ValidateResponse[];
    copyFromLastWeek: () => void;
    workDays: WorkDay[];
    allocations: Allocation[];
    recalculateActualWork?: (assignmentId: string) => void;
    onRemainingWorkUpdate?: (assignmentId: string, newRemainingWork: number) => void;
}

// TODO should come from config
const deadlineNameText = 'Deadline';

function TimePeriod(props: TimePeriodProps) {
    const { t } = useTranslation('timesheet');
    const [splitBarMouseDown, setSplitBarMouseDown] = useState<boolean>(false);
    const [splitBarStartPos, setSplitBarStartPos] = useState({ mouseStart: 0, barLastPos: 0 });
    const [timerowgroups, setTimerowsgroups] = useState<TimeRowGroup[]>([]);
    const [expanded, setExpanded] = useState<boolean>(true);
    const [groupiconrotation, setGroupiconrotation] = useState<string>('rotate(-90deg)');

    let timesheetIsReadonly = false;

    if (props.submitted) timesheetIsReadonly = true;
    if (props.periodIsClosed) timesheetIsReadonly = true;
    if (props.timesheetstate === 'Rejected') timesheetIsReadonly = false;

    const [totalarray, setTotalarray] = useState<any>(null);

    const [searchActivate, setSearchActivate] = React.useState<number>(0);

    const [perioddays, setPerioddays] = useState<PeriodDay[]>([]);
    const [headersNameOrDate, setheadersNameOrDate] = useState<'name' | 'date'>('name');
    const { splitBarPos, setSplitBarPos, plannedActive, actualActive, remainingActive, setPlannedActive, setActualActive, setRemainingActive } =
        useGridContext();
    const [splitterbarposition, setSplitterbarposition] = useState<number>(Number(splitBarPos));

    const {
        initialData: {
            configuration: {
                capacityDefault,
                workDayBoundaries: workDayBarActive,
                dateFormat,
                timesheetRAGsEnabled,
                showPlanned,
                workTypesEnabled,
                timeEntryRoundingInterval,
                showAllocations,
                weekConfig,
            },
            aggregatedUser: { user },
            calendarHolidays,
        },
    } = useConfigValuesState();

    const { allocations } = props;

    const formatAllocation = useAllocationFormatter(perioddays);
    const totalAllocationHours = useMemo(() => allocations.reduce((acc, cur) => acc + (cur.hours ?? 0), 0), [allocations]);
    const totalAllocationDisplay = useMemo(() => formatAllocation(totalAllocationHours), [formatAllocation, totalAllocationHours]);

    const userHolidays = useMemo(() => calendarHolidays.map(m => new Date(m)), [calendarHolidays]);

    const { administrativeProjects, nonAdministrativeProjects }: { administrativeProjects: TimeRowGroup[]; nonAdministrativeProjects: TimeRowGroup[] } =
        useMemo(() => {
            const result = timerowgroups.reduce(
                (acc, group) => {
                    if (group.TimeRows.filter(row => row.projectType === 'Administrative').length > 0) {
                        if (!acc.administrativeProject) {
                            // Create container for holding tasks owned by Administrative Projects
                            acc.administrativeProject = {
                                ...group,
                                GroupName: 'Administrative Projects',
                                TimeRows: [...group.TimeRows],
                            };
                        } else {
                            // Merge additional TimeRows into the single Administrative project
                            acc.administrativeProject.TimeRows.push(...group.TimeRows);
                        }
                    } else {
                        acc.nonAdministrativeProjects.push(group);
                    }
                    return acc;
                },
                { administrativeProject: null, nonAdministrativeProjects: [] },
            );

            return {
                administrativeProjects: result.administrativeProject ? [result.administrativeProject] : [],
                nonAdministrativeProjects: result.nonAdministrativeProjects,
            };
        }, [timerowgroups]);

    useEffect(() => {
        if (perioddays.length === 0) {
            return;
        }
        const sortedTasks: UserRegistrationPeriod[] = SortTimeRows(props.times);
        let timeTaskRowsCount = 0;
        const groups: TimeRowGroup[] = [];
        let group: TimeRowGroup = null;

        for (const element of sortedTasks) {
            const groupExists = groups.find(_ => _.GroupId === element.projectId);

            if (!groupExists) {
                const showProjectName = element.projectName + `${element.projectNumber ? ` (${element.projectNumber})` : ''}`;

                group = new TimeRowGroup(element.projectId, showProjectName, element.projectSource, [], true);
                groups.push(group);
                group.TimeRows.push(element);
            } else {
                const existingGroup = groups.find(_ => _.GroupId === element.projectId);
                existingGroup.TimeRows.push(element);
            }
            timeTaskRowsCount++;
        }

        setTimerowsgroups(prevState => {
            if (prevState.length > 0) {
                const previousGroups = prevState.filter(group => groups.some(ps => ps.GroupId === group.GroupId));
                const newGroups = groups.map(newGroup => {
                    const existingGroup = previousGroups.find(previousGroup => previousGroup.GroupId === newGroup.GroupId);
                    if (!existingGroup) {
                        return newGroup;
                    }
                    if (newGroup.TimeRows.length !== existingGroup.TimeRows.length) {
                        return { ...newGroup, Expanded: true };
                    }
                    return { ...newGroup, Expanded: existingGroup.Expanded };
                });

                return newGroups;
            } else {
                // Here you can set the max rows before starting as collapsed
                if (groups.length + timeTaskRowsCount > 20) {
                    setExpanded(false);
                }
                return groups;
            }
        });

        const mtotalarray = {};

        for (let pi = 0; pi < perioddays.length; pi++) {
            const pdate: number = perioddays[pi].id;
            const oldval = mtotalarray[pdate] | 0;
            mtotalarray[pdate] = oldval + GetTotalFromTimeRowInDayArray(sortedTasks, perioddays[pi].date);
        }
        setTotalarray(mtotalarray);
    }, [props.times, perioddays]);

    useEffect(() => {
        const periodDaysArr = getPeriodDays(props.startDate, props.endDate, t);
        setPerioddays(periodDaysArr);
    }, [props.startDate, props.endDate, t, weekConfig]);

    useEffect(() => {
        if (props.search > 0) {
            setSearchActivate(props.search);
            setExpanded(false);
        }
    }, [props.search]);

    useEffect(() => {
        setTimerowsgroups(groups => {
            return groups.map(g => ({ ...g, Expanded: expanded }));
        });
    }, [expanded]);

    const onCellUpdated = (
        time: UserRegistrationPeriod,
        day: PeriodDay,
        te: TimeEntry,
        value: number,
        comment: string,
        flagged: boolean,
        mode: TimeGridMode,
        previousValue: number,
        saveSuccess: Function,
        skipApiCall?: boolean,
    ): void => {
        props.onCellUpdated(time, day, te, value, comment, flagged, mode, previousValue, saveSuccess, skipApiCall);
        // let diffValue = value - previousValue;
        // let newValue = totalarray[day.id] + diffValue;
        // setTotalarray((ta) => {
        //     return { ...ta, [day.id]: newValue };
        // });
    };

    const splitbarMouseDownEvent = (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
        console.debug('splitbarMouseDownEvent');
        event.preventDefault();
        if (!splitBarMouseDown) {
            setSplitBarMouseDown(true);
            setSplitBarStartPos({ mouseStart: event.pageX, barLastPos: splitBarPos });
            setSplitterbarposition(Number(splitBarPos));
        }
    };

    const splitbarGridMouseUpEvent = (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
        setSplitBarMouseDown(false);
        console.debug('splitbarGridMouseMoveEvent');
        setSplitBarPos(splitterbarposition);
    };

    const splitbarGridMouseMoveEvent = (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
        if (splitBarMouseDown) {
            console.debug('splitbarGridMouseMoveEvent');
            const xmove = splitBarStartPos.barLastPos - (splitBarStartPos.mouseStart - event.pageX);
            if (xmove > 75 && xmove < 2000 && xmove !== splitterbarposition && xmove % 2 === 0) {
                setSplitterbarposition(xmove);
            }
        }
    };

    const groupGollapsedExpanded = (ev: React.MouseEvent<HTMLTableDataCellElement, MouseEvent>): void => {
        if (expanded) {
            setGroupiconrotation('rotate(-90deg)');
        } else {
            setGroupiconrotation('rotate(0deg)');
        }
        setExpanded(e => !e);
    };

    const PinMyTime = (time: UserRegistrationPeriod, pinned: boolean) => {
        props.pinTime(time, pinned, props.mode);
    };

    const DeleteMyTime = (time: UserRegistrationPeriod) => {
        props.deleteTime(time, perioddays);
    };

    const RenderDayTotalWithAvailabilityScore = (numberTotal: number, periodDateToRender: Date, isWeeked: boolean) => {
        const uAvailability = user.availability ?? user.calendar?.dailyCapacity ?? capacityDefault;
        if (numberTotal === undefined || numberTotal == null) {
            return <></>;
        }
        const numberTotalFormatted = NumberMax2DecimalFormatAsString(numberTotal);

        // debugger;
        if (uAvailability === null || uAvailability === undefined || uAvailability === 0 || !timesheetRAGsEnabled) {
            return (
                <Text className="totalCellInner" as="div">
                    {numberTotalFormatted}
                </Text>
            );
        }

        let iconClass = 'availabilityStatusIcon';
        if (numberTotal >= uAvailability * 0.6 && numberTotal <= uAvailability * 1.4) {
            iconClass += 'Green';
        } else if (numberTotal >= uAvailability * 0.4 && numberTotal <= uAvailability * 1.6) {
            iconClass += 'Amber';
        } else {
            iconClass += 'Red';
        }

        return (
            <Text
                className="totalCellInner"
                as="div"
                styles={{
                    root: { display: 'flex', justifyContent: 'center', alignItems: 'center' },
                }}
                data-testid="availabilityStatusContainer"
            >
                <Text
                    styles={{
                        root: { position: 'relative', fontWeight: 'inherit' },
                    }}
                >
                    {!isWeeked && (
                        <TooltipHost
                            content={
                                <div style={{ textAlign: 'left' }}>
                                    <div>{t('table.availability.title', { value: uAvailability })}</div>
                                    <div>{t('table.availability.description', { value: numberTotalFormatted })}</div>
                                    <br />
                                    <table>
                                        <tbody>
                                            <tr>
                                                <td width={45}>{t('table.red.title')}</td>
                                                <td>
                                                    {t('table.red.description', {
                                                        value: MathToFixed(uAvailability * 0.4, 1),
                                                        value2: MathToFixed(uAvailability * 1.6, 1),
                                                    })}
                                                </td>
                                            </tr>
                                            <tr>
                                                <td>{t('table.amber.title')}</td>
                                                <td>
                                                    {t('table.amber.description', {
                                                        value: MathToFixed(uAvailability * 0.4, 1),
                                                        value2: MathToFixed(uAvailability * 0.6 - 0.1, 1),
                                                        value3: MathToFixed(uAvailability * 1.4 + 0.1, 1),
                                                        value4: MathToFixed(uAvailability * 1.6, 1),
                                                    })}
                                                </td>
                                            </tr>
                                            <tr>
                                                <td>{t('table.green.title')}</td>
                                                <td>
                                                    {t('table.green.description', {
                                                        value: MathToFixed(uAvailability * 0.6, 1),
                                                        value2: MathToFixed(uAvailability * 1.4, 1),
                                                    })}
                                                </td>
                                            </tr>
                                        </tbody>
                                    </table>
                                </div>
                            }
                            closeDelay={250}
                            styles={{
                                root: { position: 'absolute', right: '100%' },
                            }}
                        >
                            <FontIcon className={iconClass} iconName="StatusCircleOuter" />
                        </TooltipHost>
                    )}
                    {numberTotalFormatted}
                </Text>
            </Text>
        );
    };

    // const RenderPlannedActive = () => {
    //     return (
    //         <Checkbox label="Planned" checked={uiCtx.plannedActive} onChange={() => {uiCtx.setPlannedActive(!uiCtx.plannedActive)}} />
    //     );
    // }

    /*if (!loadingcomplete)
        return <QuotesLoader />;*/
    const tooltipTriggerId = useId('tooltipTrigger');
    const toggleHeaders = useCallback(() => setheadersNameOrDate(state => (state === 'date' ? 'name' : 'date')), []);
    return (
        <div className="timeperiod" onMouseUp={splitbarGridMouseUpEvent} onMouseMove={splitbarGridMouseMoveEvent}>
            <ScrollablePane>
                <div className="tableWrapper">
                    {/* <RenderStatuses /> */}
                    <table>
                        <thead>
                            {workDayBarActive && (
                                <WorkDayBar
                                    reportingPeriodId={props.reportPeriodId}
                                    workDays={props.workDays}
                                    periodDays={perioddays}
                                    deadlineDate={props.deadlineDate}
                                    today={props.today}
                                    startDate={props.startDate}
                                />
                            )}
                            <tr className={`noselect topHeadRow${props.today.getTime() === props.startDate.getTime() ? ' isMonday' : ''}`}>
                                <th className="headerContainer noselect">
                                    <Stack horizontal className="headerCell" styles={{ root: { flexWrap: 'nowrap' } }}>
                                        <div className="expander" onClick={groupGollapsedExpanded}>
                                            <div className="expanderInner" data-testid={`allTasksExpander${expanded ? 'Expanded' : 'Collapsed'}`}>
                                                <FontIcon
                                                    iconName="ChevronDown"
                                                    style={{
                                                        transform: groupiconrotation,
                                                        transition: 'transform .2s ease 0s',
                                                    }}
                                                />
                                            </div>
                                        </div>
                                        <Text className="groupName textOverflow">{t('table.all')}</Text>
                                        <IconButton
                                            id={`tft-utils`}
                                            menuIconProps={{ iconName: 'More' }}
                                            menuProps={
                                                showPlanned
                                                    ? {
                                                          items: [
                                                              {
                                                                  key: 'copyFromLastWeek',
                                                                  text: t('table.status.copyFromLastWeek'),
                                                                  iconProps: { iconName: 'Copy' },
                                                                  disabled: timesheetIsReadonly,
                                                                  onClick: () => props.copyFromLastWeek(),
                                                              },
                                                              {
                                                                  key: 'plannedActive',
                                                                  text: t('table.status.plannedActive'),
                                                                  iconProps: { iconName: plannedActive ? 'CheckboxCompositeReversed' : 'Checkbox' },
                                                                  onClick: () => setPlannedActive(!plannedActive),
                                                              },
                                                              {
                                                                  key: 'actualActive',
                                                                  text: t('table.status.actualActive'),
                                                                  iconProps: { iconName: actualActive ? 'CheckboxCompositeReversed' : 'Checkbox' },
                                                                  onClick: () => setActualActive(!actualActive),
                                                              },
                                                              {
                                                                  key: 'remainingActive',
                                                                  text: t('table.status.remainingActive'),
                                                                  iconProps: { iconName: remainingActive ? 'CheckboxCompositeReversed' : 'Checkbox' },
                                                                  onClick: () => setRemainingActive(!remainingActive),
                                                              },
                                                          ],

                                                          // By default, the menu will be focused when it opens. Uncomment the next line to prevent this.
                                                          // shouldFocusOnMount: false
                                                      }
                                                    : {
                                                          items: [
                                                              {
                                                                  key: 'copyFromLastWeek',
                                                                  text: t('table.status.copyFromLastWeek'),
                                                                  iconProps: { iconName: 'Copy' },
                                                                  disabled: timesheetIsReadonly,
                                                                  onClick: () => props.copyFromLastWeek(),
                                                              },
                                                          ],

                                                          // By default, the menu will be focused when it opens. Uncomment the next line to prevent this.
                                                          // shouldFocusOnMount: false
                                                      }
                                            }
                                        ></IconButton>
                                        {workTypesEnabled && (
                                            <div className="adjustwidthsplitter" style={{ position: 'absolute', right: 0, height: '100%' }}>
                                                <div onMouseDown={splitbarMouseDownEvent}>
                                                    <div className="splitbar"></div>
                                                </div>
                                            </div>
                                        )}
                                    </Stack>
                                </th>
                                {/* {uiCtx.workTypeEnabled && ( */}
                                <th hidden={!workTypesEnabled} className="headerContainer noselect">
                                    <Stack
                                        horizontal
                                        className="headerCell"
                                        verticalAlign="center"
                                        horizontalAlign="center"
                                        styles={{ root: { flexWrap: 'nowrap', height: '100%', position: 'relative' } }}
                                    >
                                        <Text as="div" className="headerCell workType" style={{ flexGrow: 1 }}>
                                            {t('table.workType')}
                                        </Text>
                                        <div className="adjustwidthsplitter" style={{ position: 'absolute', right: 0, height: '100%' }}>
                                            <div onMouseDown={splitbarMouseDownEvent}>
                                                <div className="splitbar"></div>
                                            </div>
                                        </div>
                                    </Stack>
                                </th>
                                {/* )} */}
                                {plannedActive && showPlanned && (
                                    <th className="headerContainer noselect">
                                        <Stack
                                            horizontal
                                            className="headerCell"
                                            verticalAlign="center"
                                            horizontalAlign="center"
                                            styles={{ root: { flexWrap: 'nowrap', height: '100%', position: 'relative' } }}
                                        >
                                            <Text as="div" className="headerCell planned" style={{ flexGrow: 1 }}>
                                                {t('table.plannedWork')}
                                            </Text>
                                        </Stack>
                                    </th>
                                )}
                                {actualActive && showPlanned && (
                                    <th className="headerContainer noselect">
                                        <Stack
                                            horizontal
                                            className="headerCell"
                                            verticalAlign="center"
                                            horizontalAlign="center"
                                            styles={{ root: { flexWrap: 'nowrap', height: '100%', position: 'relative' } }}
                                        >
                                            <Text as="div" className="headerCell actual" style={{ flexGrow: 1 }}>
                                                {t('table.actualWork')}
                                            </Text>
                                        </Stack>
                                    </th>
                                )}
                                {remainingActive && showPlanned && (
                                    <th className="headerContainer noselect">
                                        <Stack
                                            horizontal
                                            className="headerCell"
                                            verticalAlign="center"
                                            horizontalAlign="center"
                                            styles={{ root: { flexWrap: 'nowrap', height: '100%', position: 'relative' } }}
                                        >
                                            <Text as="div" className="headerCell remaining" style={{ flexGrow: 1 }}>
                                                {t('table.remainingWork')}
                                            </Text>
                                        </Stack>
                                    </th>
                                )}
                                {/* <th className="columnSpacer"><div /></th> */}

                                {perioddays.map(date => (
                                    <DayHeader
                                        date={date}
                                        deadlineDate={props.deadlineDate}
                                        headersNameOrDate={headersNameOrDate}
                                        today={props.today}
                                        toggleHeaders={toggleHeaders}
                                        key={date.id}
                                        userHolidays={userHolidays}
                                    />
                                ))}
                                <th className="headerContainer rowTotal totalRow noselect">
                                    <Text as="div" className="headerCell rowTotal">
                                        {t('table.periodTotal')}
                                    </Text>
                                </th>
                                {showAllocations && (
                                    <th className="headerContainer rowTotal noselect">
                                        <Text as="div" className="headerCell rowTotal">
                                            Allocation
                                        </Text>
                                    </th>
                                )}
                            </tr>
                        </thead>
                        <tbody>
                            {[...administrativeProjects, ...nonAdministrativeProjects].map(group => {
                                return (
                                    <Fragment key={group.GroupId}>
                                        <TimePeriodGroup
                                            group={group}
                                            today={props.today}
                                            expanded={group.Expanded}
                                            onCellUpdated={onCellUpdated}
                                            totalCellUpdated={props.totalCellUpdated}
                                            mode={props.mode}
                                            pinMyTime={PinMyTime}
                                            deleteMyTime={DeleteMyTime}
                                            submitted={props.submitted}
                                            periodIsClosed={props.periodIsClosed}
                                            timeLowerLimitConfig={String(timeEntryRoundingInterval)}
                                            startDate={props.startDate}
                                            endDate={props.endDate}
                                            deadlineDate={props.deadlineDate}
                                            perioddays={perioddays}
                                            reportPeriodId={props.reportPeriodId}
                                            updateWorkType={props.updateWorkType}
                                            allocations={allocations}
                                            showAllocations={showAllocations}
                                            state={props.timesheetstate}
                                            recalculateActualWork={props.recalculateActualWork}
                                            onRemainingWorkUpdate={props.onRemainingWorkUpdate}
                                            allTimes={props.times}
                                        />
                                        <tr className="rowSpacer" />
                                        {/* {index !== timerowgroups.length - 1 && <tr className="rowSpacer" />} */}
                                    </Fragment>
                                );
                            })}
                        </tbody>
                        <tfoot>
                            <tr className={`noselect totalRow${props.today === props.startDate ? ' isMonday' : ''}`}>
                                <td
                                    className="rowHeader noselect"
                                    style={
                                        workTypesEnabled
                                            ? { width: splitterbarposition }
                                            : { width: splitterbarposition > 800 ? splitterbarposition - 400 : splitterbarposition / 2 }
                                    }
                                >
                                    <Stack horizontal className="rowHeaderContainer">
                                        <div className="expander" />
                                        <Text className="groupName">{t('table.total')}</Text>
                                    </Stack>
                                </td>
                                <td
                                    hidden={!workTypesEnabled}
                                    className="cellContainer worktypeContainer noselect"
                                    style={
                                        workTypesEnabled ? { width: splitterbarposition } : { width: splitterbarposition > 800 ? 400 : splitterbarposition / 2 }
                                    }
                                >
                                    <Stack horizontal className="cell">
                                        <div className="expander" />
                                        <Text className="groupName">{/* Empty for work type */}</Text>
                                    </Stack>
                                </td>
                                {plannedActive && showPlanned && (
                                    <td className="cellContainer worktypeContainer noselect">
                                        <Stack horizontal className="cell">
                                            <div className="expander" />
                                            <Text className="groupName">{/* Empty for Planned */}</Text>
                                        </Stack>
                                    </td>
                                )}
                                {actualActive && showPlanned && (
                                    <td className="cellContainer worktypeContainer noselect">
                                        <Stack horizontal className="cell">
                                            <div className="expander" />
                                            <Text className="groupName">{/* Empty for Actual */}</Text>
                                        </Stack>
                                    </td>
                                )}
                                {remainingActive && showPlanned && (
                                    <td className="cellContainer worktypeContainer noselect">
                                        <Stack horizontal className="cell">
                                            <div className="expander" />
                                            <Text className="groupName">{/* Empty for Remaining */}</Text>
                                        </Stack>
                                    </td>
                                )}

                                {totalarray &&
                                    perioddays.map((periodday, index) => {
                                        return (
                                            <td
                                                key={periodday.id}
                                                className={`noselect${IsWeekend(periodday.date) ? ' weekend' : ''}${
                                                    IsDatesNoTimeEqual(props.today, periodday.date) ? ' today' : ''
                                                }`}
                                            >
                                                <div className="totalCell">
                                                    {RenderDayTotalWithAvailabilityScore(totalarray[periodday.id], periodday.date, IsWeekend(periodday.date))}
                                                </div>
                                            </td>
                                        );
                                    })}
                                {totalarray && (
                                    <td className="rowTotal periodTotal noselect">
                                        <div className="totalCell">
                                            <Text as="div" className="totalCellInner">
                                                {FormatValue(Number(Object.values(totalarray).reduce((acc: number, cur: number) => acc + cur, 0)))}
                                            </Text>
                                        </div>
                                    </td>
                                )}
                                {showAllocations && (
                                    // allocation column
                                    <td className="rowTotal periodTotal noselect">
                                        <div className="totalCell" style={{ position: 'relative' }}>
                                            <Text as="div" className="totalCellInner">
                                                {totalAllocationDisplay}
                                            </Text>
                                            <TooltipHost
                                                content="The total might appear higher than the timesheet allocations because some projects are not included in this view. To see the missing projects and hours, click submit. You will then receive a list of projects with missing time entries to match the allocation."
                                                calloutProps={{
                                                    directionalHint: DirectionalHint.bottomLeftEdge,
                                                    target: `#${tooltipTriggerId}`,
                                                    calloutMaxWidth: 200,
                                                }}
                                            >
                                                <IconButton iconProps={{ iconName: 'Info' }} id={tooltipTriggerId} styles={iconButtonStyle} />
                                            </TooltipHost>
                                        </div>
                                    </td>
                                )}
                            </tr>
                        </tfoot>
                    </table>
                </div>
            </ScrollablePane>
        </div>
    );
}

export default TimePeriod;

const DayHeader = ({
    date,
    deadlineDate,
    toggleHeaders,
    headersNameOrDate,
    today,
    userHolidays,
}: {
    date: PeriodDay;
    deadlineDate: Date;
    toggleHeaders: () => void;
    headersNameOrDate: 'name' | 'date';
    today: Date;
    userHolidays: Date[];
}) => {
    const {
        initialData: {
            configuration: { shortDateFormat, weekConfig },
            aggregatedUser: { user },
        },
    } = useConfigValuesState();

    const shortDateHeaderText = useMemo(() => {
        const currentDate = new Date(date.date);
        return format(currentDate, shortDateFormat);
    }, [date.date, shortDateFormat]);

    const render = useMemo(() => {
        if (IsDeadline(date.date, deadlineDate)) {
            if (IsHoliday(date.date, userHolidays)) {
                return (
                    <TooltipHost
                        directionalHint={DirectionalHint.topCenter}
                        content={(GetHolidayName(date.date, user.calendar)?.name ?? date.translatedName) + ` (${date.translatedName})`}
                        closeDelay={400}
                    >
                        <Text as="div" className="headerCell day" onClick={toggleHeaders}>
                            {headersNameOrDate === 'name' ? deadlineNameText : shortDateHeaderText}
                        </Text>
                    </TooltipHost>
                );
            } else {
                return (
                    <TooltipHost directionalHint={DirectionalHint.topCenter} content={date.translatedName} closeDelay={400}>
                        <Text as="div" className="headerCell day" onClick={toggleHeaders}>
                            {headersNameOrDate === 'name' ? deadlineNameText : shortDateHeaderText}
                        </Text>
                    </TooltipHost>
                );
            }
        } else if (IsHoliday(date.date, userHolidays)) {
            return (
                <TooltipHost directionalHint={DirectionalHint.topCenter} content={date.translatedName} closeDelay={400}>
                    <Text as="div" className="headerCell day" onClick={toggleHeaders}>
                        {headersNameOrDate === 'name' ? GetHolidayName(date.date, user.calendar)?.name ?? date.translatedName : shortDateHeaderText}
                    </Text>
                </TooltipHost>
            );
        }
        return (
            <Text as="div" className="headerCell day" onClick={toggleHeaders}>
                {headersNameOrDate === 'name' ? date.translatedName : shortDateHeaderText}
            </Text>
        );
    }, [date, shortDateHeaderText, deadlineDate, headersNameOrDate, toggleHeaders, user.calendar]);

    const isDayEnabled = useMemo(() => {
        const name = date.translatedName.toLowerCase();
        return weekConfig.find(day => day.name === name)?.enabled ?? false;
    }, [date.translatedName, weekConfig]);

    return (
        <th
            // key={date.date}
            className={`headerContainer noselect${IsWeekend(date.date) || !isDayEnabled ? ' weekend' : ''}${
                IsDeadline(date.date, deadlineDate) ? ' deadline' : ''
            }${IsHoliday(date.date, userHolidays) ? ' holiday' : ''}${IsDatesNoTimeEqual(today, date.date) ? ' today' : ''}`}
        >
            {render}
        </th>
    );
};

const iconButtonStyle: IButtonStyles = {
    root: {
        width: 20,
        height: 20,
        position: 'absolute',
        top: 5,
        right: 10,
    },
    rootHovered: {
        backgroundColor: 'transparent',
        color: 'rgb(96, 97, 170)',
    },
    icon: {
        fontSize: 14,
    },
};
